// Coquille de l'espace Administration (charte « Séance de décision ») : en-tête avec la marque,
// le sélecteur de collectivité, la navigation principale et le menu Plus, « Aller à… » (Ctrl K),
// la bascule jour et nuit, le menu du compte, et le bandeau hors connexion.
import { Bandeau, Coquille, IndicateurEnvironnement, Lien, Marque } from '@cc/ui';
import { onlineManager } from '@tanstack/react-query';
import { ArrowLeftRight, ChevronDown, LogOut, Moon, Search, SlidersHorizontal, Sun, UserRound } from 'lucide-react';
import { useEffect, useRef, useSyncExternalStore } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { useCollectivite, useModules, useUtilisateurs } from '../api/requetes';
import { usePreferences } from '../preferences/FournisseurPreferences';
import { useSession } from '../session/FournisseurSession';
import { nomCompte } from '../session/permissions';
import { entreeActive, entreesNavigation, entreesRapides } from './navigation';
import styles from './coquille.module.css';

function useEnLigne(): boolean {
  return useSyncExternalStore(
    (rappel) => onlineManager.subscribe(rappel),
    () => onlineManager.isOnline(),
    () => true,
  );
}

/** « Aller à… » : ouvre la recherche globale ; Ctrl K (ou Cmd K) depuis n'importe quel écran. */
function AllerA() {
  const navigate = useNavigate();
  useEffect(() => {
    const raccourci = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        navigate('/recherche');
      }
    };
    window.addEventListener('keydown', raccourci);
    return () => window.removeEventListener('keydown', raccourci);
  }, [navigate]);
  return (
    <button type="button" className={styles.allerA} onClick={() => navigate('/recherche')}>
      <Search aria-hidden strokeWidth={2} width={14} height={14} />
      <span className={styles.allerALibelle}>Aller à…</span>
      <span className="cc-raccourci" aria-hidden>
        Ctrl K
      </span>
      <span className="cc-masque">, raccourci Ctrl K</span>
    </button>
  );
}

/** Bascule jour et nuit : enregistrée dans les préférences (thème système rétabli depuis la page Préférences). */
function BasculeTheme() {
  const { preferences, modifier } = usePreferences();
  const sombre =
    preferences.theme === 'nuit' || (preferences.theme !== 'jour' && typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches);
  return (
    <button
      type="button"
      className={styles.boutonIcone}
      aria-label={sombre ? 'Passer en mode jour' : 'Passer en mode nuit'}
      onClick={() => void modifier({ theme: sombre ? 'jour' : 'nuit' })}
    >
      {sombre ? <Sun aria-hidden strokeWidth={1.8} width={16} height={16} /> : <Moon aria-hidden strokeWidth={1.8} width={16} height={16} />}
    </button>
  );
}

function MenuCompte() {
  const { contexte, deconnecter } = useSession();
  const navigate = useNavigate();
  const ref = useRef<HTMLDetailsElement>(null);
  const location = useLocation();
  useEffect(() => {
    if (ref.current) ref.current.open = false;
  }, [location.pathname]);
  const nom = nomCompte(contexte) || contexte?.compte.identifiant || 'Compte';
  const initiales = nom
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((m) => m[0]?.toUpperCase())
    .join('');
  return (
    <details className={styles.compte} ref={ref}>
      <summary aria-label={`${initiales ? `${initiales}, ` : ''}compte : ${nom}`}>
        <span className={styles.avatar} aria-hidden>
          {initiales || <UserRound strokeWidth={1.75} width={16} height={16} />}
        </span>
      </summary>
      <div className={styles.menu}>
        <p>
          <strong>{nom}</strong>
          <br />
          {contexte?.compte.identifiant}
        </p>
        <Lien href="/preferences" className={styles.menuLien}>
          <SlidersHorizontal aria-hidden strokeWidth={1.75} width={16} height={16} />
          Préférences
        </Lien>
        <Lien href="/contexte" className={styles.menuLien}>
          <ArrowLeftRight aria-hidden strokeWidth={1.75} width={16} height={16} />
          Changer d&apos;espace de travail
        </Lien>
        <button type="button" className={styles.menuLien} onClick={() => void deconnecter().then(() => navigate('/connexion', { replace: true }))}>
          <LogOut aria-hidden strokeWidth={1.75} width={16} height={16} />
          Se déconnecter
        </button>
      </div>
    </details>
  );
}

/** Sélecteur de collectivité : nom de l'espace actif ; ouvre le choix d'espace si le compte en a plusieurs. */
function SelecteurCollectivite({ nom, plusieurs }: { nom: string; plusieurs: boolean }) {
  if (!plusieurs) {
    return (
      <span className={styles.espaceActif} data-testid="espace-actif">
        {nom}
      </span>
    );
  }
  return (
    <Lien href="/contexte" className={styles.selecteur} aria-label={`Espace actif : ${nom}. Changer d'espace`}>
      <span data-testid="espace-actif">{nom}</span>
      <ChevronDown aria-hidden strokeWidth={1.75} width={14} height={14} />
    </Lien>
  );
}

export function CoquilleAdministration() {
  const { contexte } = useSession();
  const location = useLocation();
  const modules = useModules();
  const collectivite = useCollectivite();
  const utilisateurs = useUtilisateurs();
  const enLigne = useEnLigne();

  const enAttente = (utilisateurs.data ?? []).reduce((n, u) => n + u.roles.filter((r) => r.statut === 'en_attente_validation').length, 0);
  const navigation = entreesNavigation(contexte, modules.data, {
    utilisateurs: enAttente > 0 ? { nombre: enAttente, libelle: 'attribution(s) en attente de validation' } : undefined,
  });
  const actif = entreeActive(location.pathname);
  const nomEspace =
    collectivite.data?.nom ??
    contexte?.appartenances.find((a) => a.id === contexte.appartenance_active?.id)?.collectivite?.nom ??
    'Espace de travail';
  const plusieurs = (contexte?.appartenances.filter((a) => a.statut === 'active' || a.statut === 'invitee').length ?? 0) > 1;

  return (
    <Coquille
      marque={<Marque href="/" image="/marque-collectivitesconnect.png" imageNuit="/marque-collectivitesconnect-fond-sombre.png" />}
      contexte={<SelecteurCollectivite nom={nomEspace} plusieurs={plusieurs} />}
      entrees={navigation.principales}
      entreesSecondaires={navigation.secondaires}
      entreesRapides={entreesRapides(navigation)}
      actif={actif}
      cleNavigation={location.pathname}
      actions={
        <>
          <AllerA />
          <BasculeTheme />
          <MenuCompte />
        </>
      }
      pied={
        <>
          <span>{nomEspace}</span>
          <IndicateurEnvironnement environnement={import.meta.env.VITE_CC_ENVIRONNEMENT} />
        </>
      }
      bandeaux={!enLigne ? <Bandeau type="horsligne" /> : null}
    >
      <Outlet />
    </Coquille>
  );
}
